import React, { useEffect, useState } from 'react';
import { Eye, CheckSquare, Link as LinkIcon, BarChart3 } from 'lucide-react';
import { mockWidgetsByAgent } from '../data/mockData';
import { Widget, WidgetCategory } from '../types';
import { useAgent } from '../contexts/AgentContext';
import { AppCard } from '../components/base/AppCard';
import { AppBadge } from '../components/base/AppBadge';
import { AppSwitch } from '../components/base/AppSwitch';
export function WidgetsPage() {
  const {
    activeAgentId
  } = useAgent();
  const [widgets, setWidgets] = useState<Widget[]>([]);
  useEffect(() => {
    const agentWidgets = mockWidgetsByAgent[activeAgentId] || mockWidgetsByAgent['default'];
    setWidgets(agentWidgets.widgets);
  }, [activeAgentId]);
  const handleToggle = (widgetId: string) => {
    setWidgets(prevWidgets => prevWidgets.map(widget => widget.id === widgetId ? {
      ...widget,
      enabled: !widget.enabled
    } : widget));
  };
  const getCategoryBadgeVariant = (category: WidgetCategory): 'blue' | 'gray' => {
    return 'blue';
  };
  return <div className="pb-12 space-y-8" data-id="element-2155">
      {/* Widgets List */}
      <div className="space-y-6 max-w-3xl mx-auto" data-id="element-2156">
        {widgets.map(widget => <WidgetCard key={widget.id} widget={widget} onToggle={() => handleToggle(widget.id)} getCategoryBadgeVariant={getCategoryBadgeVariant} data-id="element-2157" />)}
      </div>
    </div>;
}
interface WidgetCardProps {
  widget: Widget;
  onToggle: () => void;
  getCategoryBadgeVariant: (category: WidgetCategory) => 'blue' | 'gray';
}
function WidgetCard({
  widget,
  onToggle,
  getCategoryBadgeVariant
}: WidgetCardProps) {
  return <AppCard className={`transition-all duration-200 ${widget.enabled ? 'ring-1 ring-blue-100 dark:ring-blue-900/30 shadow-md' : 'opacity-90'}`} padding="none" data-id="element-2158">
      {/* Header */}
      <div className="p-6 border-b border-gray-100 dark:border-dark-subtle" data-id="element-2159">
        <div className="flex justify-between items-start mb-4" data-id="element-2160">
          <div className="flex items-center gap-3" data-id="element-2161">
            <h3 className="text-[17px] font-semibold text-gray-900 dark:text-gray-100" data-id="element-2162">
              {widget.name}
            </h3>
            <AppBadge variant={getCategoryBadgeVariant(widget.category)} data-id="element-2163">
              {widget.category}
            </AppBadge>
          </div>
          <AppSwitch checked={widget.enabled} onChange={onToggle} data-id="element-2164" />
        </div>

        <p className="text-[15px] text-gray-600 dark:text-gray-400 leading-relaxed" data-id="element-2165">
          {widget.description}
        </p>
      </div>

      {/* Preview */}
      <div className="p-6 bg-gray-50/50 dark:bg-dark-subtle/30" data-id="element-2166">
        <div className="flex items-center mb-3" data-id="element-2167">
          <Eye className="w-4 h-4 text-gray-400 dark:text-gray-500 mr-2" data-id="element-2168" />
          <span className="text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider" data-id="element-2169">
            Anteprima
          </span>
        </div>

        <div className="bg-white dark:bg-dark-surface rounded-xl border border-gray-200 dark:border-dark-medium p-5 shadow-sm" data-id="element-2170">
          <WidgetPreview widget={widget} data-id="element-2171" />
        </div>
      </div>
    </AppCard>;
}
function WidgetPreview({
  widget
}: {
  widget: Widget;
}) {
  switch (widget.key) {
    case 'checklist':
      return <ChecklistPreview data={widget.previewExample} data-id="element-2172" />;
    case 'link_preview':
      return <LinkPreviewWidget data={widget.previewExample} data-id="element-2173" />;
    case 'chart':
      return <ChartPreview data={widget.previewExample} data-id="element-2174" />;
    case 'poll':
      return <PollPreview data={widget.previewExample} data-id="element-2175" />;
    case 'quote':
      return <QuotePreview data={widget.previewExample} data-id="element-2176" />;
    case 'cta_edit_profile':
      return <CTAPreview data={widget.previewExample} data-id="element-2177" />;
    default:
      return <div className="text-sm text-gray-500" data-id="element-2178">Anteprima non disponibile</div>;
  }
}
function ChecklistPreview({
  data
}: {
  data: any;
}) {
  return <div data-id="element-2179">
      <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3" data-id="element-2180">
        {data.title}
      </h4>
      <div className="space-y-2.5" data-id="element-2181">
        {data.items.map((item: any) => <div key={item.id} className="flex items-center" data-id="element-2182">
            <div className={`w-4 h-4 rounded border flex items-center justify-center mr-3 transition-colors ${item.checked ? 'bg-blue-600 border-blue-600' : 'border-gray-300 dark:border-gray-600'}`} data-id="element-2183">
              {item.checked && <CheckSquare className="w-3 h-3 text-white" data-id="element-2184" />}
            </div>
            <span className={`text-sm ${item.checked ? 'text-gray-400 line-through' : 'text-gray-700 dark:text-gray-300'}`} data-id="element-2185">
              {item.text}
            </span>
          </div>)}
      </div>
    </div>;
}
function LinkPreviewWidget({
  data
}: {
  data: any;
}) {
  return <div className="flex items-start space-x-4" data-id="element-2186">
      {data.thumbnail && <div className="w-16 h-16 bg-blue-50 dark:bg-blue-900/20 rounded-lg flex-shrink-0 flex items-center justify-center border border-blue-100 dark:border-blue-900/30" data-id="element-2187">
          <LinkIcon className="w-6 h-6 text-blue-500 dark:text-blue-400" data-id="element-2188" />
        </div>}
      <div className="flex-1 min-w-0" data-id="element-2189">
        <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1 truncate" data-id="element-2190">
          {data.title}
        </h4>
        <p className="text-xs text-gray-600 dark:text-gray-400 mb-1.5 line-clamp-2" data-id="element-2191">
          {data.description}
        </p>
        <a href="#" className="text-xs text-blue-600 dark:text-blue-400 hover:underline truncate block" data-id="element-2192">
          {data.url}
        </a>
      </div>
    </div>;
}
function ChartPreview({
  data
}: {
  data: any;
}) {
  const maxValue = Math.max(...data.data.map((d: any) => d.value));
  return <div data-id="element-2193">
      <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-4" data-id="element-2194">
        {data.title}
      </h4>
      <div className="flex items-end justify-between space-x-3 h-24" data-id="element-2195">
        {data.data.map((item: any, index: number) => {
        const height = item.value / maxValue * 100;
        return <div key={index} className="flex-1 flex flex-col items-center" data-id="element-2196">
              <div className="w-full bg-gray-100 dark:bg-dark-medium rounded-t-md relative h-full" data-id="element-2197">
                <div className="absolute bottom-0 w-full bg-blue-500 dark:bg-blue-600 rounded-t-md transition-all" style={{
              height: `${height}%`
            }} data-id="element-2198"></div>
              </div>
              <span className="text-[10px] text-gray-500 dark:text-gray-400 mt-2 font-medium" data-id="element-2199">
                {item.label}
              </span>
            </div>;
      })}
      </div>
    </div>;
}
function PollPreview({
  data
}: {
  data: any;
}) {
  return <div data-id="element-2200">
      <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3" data-id="element-2201">
        {data.question}
      </h4>
      <div className="space-y-2" data-id="element-2202">
        {data.options.map((option: any) => <div key={option.id} className="flex items-center" data-id="element-2203">
            <div className="w-4 h-4 rounded-full border-2 border-gray-300 dark:border-gray-600 mr-3 flex-shrink-0" data-id="element-2204"></div>
            <span className="text-sm text-gray-700 dark:text-gray-300" data-id="element-2205">
              {option.text}
            </span>
          </div>)}
      </div>
    </div>;
}
function QuotePreview({
  data
}: {
  data: any;
}) {
  return <div className="border-l-4 border-blue-500 pl-4 py-1" data-id="element-2206">
      <p className="text-sm text-gray-700 dark:text-gray-300 italic mb-2 leading-relaxed" data-id="element-2207">
        &ldquo;{data.text}&rdquo;
      </p>
      <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-2208">
        — {data.source}
        {data.author && <span className="font-medium" data-id="element-2209"> ({data.author})</span>}
      </p>
    </div>;
}
function CTAPreview({
  data
}: {
  data: any;
}) {
  return <div className="text-center py-2" data-id="element-2210">
      <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2" data-id="element-2211">
        {data.title}
      </h4>
      <p className="text-xs text-gray-600 dark:text-gray-400 mb-4" data-id="element-2212">
        {data.message}
      </p>
      <button className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors duration-180 shadow-sm" data-id="element-2213">
        {data.buttonText}
      </button>
    </div>;
}